/**
 * Centralized Card Games Registry
 * Single source of truth for all card game IDs, slugs, names, icons, and aliases.
 */

export interface CardGameDefinition {
  id: number;
  slug: string;
  name: string;
  shortName?: string;
  icon: string;
  sauce: 'homura' | 'kyubey';
  aliases: string[];
  cardNexusName?: string;
  cardNexusAliases?: string[];
}

export const CARD_GAMES: readonly CardGameDefinition[] = [
  {
    id: 1,
    slug: 'one-piece',
    name: 'One Piece',
    shortName: 'One Piece',
    cardNexusName: 'One Piece Card Game',
    cardNexusAliases: [
      'One Piece Card Game',
      'one-piece-card-game',
      'One Piece TCG',
      'one-piece-tcg',
      'One Piece',
      'one-piece',
      'OPTCG'
    ],
    icon: 'fa-anchor',
    sauce: 'homura',
    aliases: [
      'one piece',
      'one-piece',
      'onepiece',
      'optcg',
      'op',
      'op-tcg',
      'one piece card game',
      'one-piece-card-game',
      'one piece tcg',
      'one-piece-tcg',
      'one piece cardgame',
      'one-piece-cardgame',
      '1'
    ]
  },
  {
    id: 2,
    slug: 'digimon',
    name: 'Digimon',
    shortName: 'Digimon',
    cardNexusName: 'Digimon Card Game',
    cardNexusAliases: [
      'Digimon Card Game',
      'digimon-card-game',
      'Digimon TCG',
      'digimon-tcg',
      'Digimon',
      'digimon',
      'DTCG'
    ],
    icon: 'fa-dragon',
    sauce: 'homura',
    aliases: [
      'digimon',
      'dtcg',
      'digimon card game',
      'digimon-card-game',
      'digimon tcg',
      'digimon-tcg',
      'digimon cardgame',
      'digimon-cardgame',
      '2'
    ]
  },
  {
    id: 3,
    slug: 'pokemon',
    name: 'Pokémon',
    shortName: 'Pokémon',
    cardNexusName: 'Pokemon',
    cardNexusAliases: [
      'Pokemon',
      'Pokémon',
      'pokemon',
      'Pokemon TCG',
      'Pokémon TCG',
      'pokemon-tcg',
      'Pokemon Card Game',
      'Pokémon Card Game',
      'pokemon-card-game',
      'PTCGL',
      'PTCGO'
    ],
    icon: 'fa-bolt',
    sauce: 'homura',
    aliases: [
      'pokémon',
      'pokemon',
      'ptcgl',
      'ptcgo',
      'poke',
      'poké',
      'pokemon tcg',
      'pokémon tcg',
      'pkmn',
      'pokemon card game',
      'pokémon card game',
      'pokemon-tcg',
      'pokémon-tcg',
      'pok-mon',
      'pocket',
      '3'
    ]
  },
  {
    id: 4,
    slug: 'magic',
    name: 'Magic: The Gathering',
    shortName: 'Magic',
    cardNexusName: 'Magic: The Gathering',
    cardNexusAliases: [
      'Magic: The Gathering',
      'Magic The Gathering',
      'magic-the-gathering',
      'Magic',
      'magic',
      'Magic TCG',
      'magic-tcg',
      'MTG',
      'MTGA',
      'MTGO'
    ],
    icon: 'fa-wand-sparkles',
    sauce: 'kyubey',
    aliases: [
      'magic',
      'magic: the gathering',
      'magic the gathering',
      'mtg',
      'mtga',
      'mtgo',
      'gathering',
      'magic-the-gathering',
      'magic tcg',
      'magic-tcg',
      '4'
    ]
  },
  {
    id: 5,
    slug: 'gundam',
    name: 'Gundam',
    shortName: 'Gundam',
    cardNexusName: 'Gundam Card Game',
    cardNexusAliases: [
      'Gundam Card Game',
      'gundam-card-game',
      'Gundam Cross War',
      'gundam-cross-war',
      'Gundam',
      'gundam',
      'Gundam TCG',
      'gundam-tcg',
      'GCG'
    ],
    icon: 'fa-robot',
    sauce: 'homura',
    aliases: [
      'gundam',
      'gundam card game',
      'gundam-card-game',
      'gundam cross war',
      'gundam-cross-war',
      'gcg',
      'gundam-tcg',
      'gundam tcg',
      'gundam cardgame',
      '5',
      '7' // legacy fallback
    ]
  },
  {
    id: 6,
    slug: 'dragon-ball-fusion',
    name: 'Dragon Ball Fusion World',
    shortName: 'Dragon Ball',
    cardNexusName: 'Dragon Ball Super Fusion World',
    cardNexusAliases: [
      'Dragon Ball Super Fusion World',
      'dragon-ball-super-fusion-world',
      'Dragon Ball Fusion World',
      'dragon-ball-fusion-world',
      'Dragon Ball Super Card Game',
      'dragon-ball-super-card-game',
      'Dragon Ball Super',
      'dragon-ball-super',
      'Dragon Ball',
      'dragon-ball',
      'dragon-ball-fusion',
      'DBFW',
      'DBS'
    ],
    icon: 'fa-circle-dot',
    sauce: 'homura',
    aliases: [
      'dragon ball',
      'dragon ball fusion world',
      'dragon-ball-fusion-world',
      'dragon-ball-fusion',
      'dragon ball super fusion world',
      'dragon ball super',
      'dragon-ball-super',
      'dragon ball super card game',
      'dbs',
      'dbfw',
      'dragonball',
      'fusion world',
      'dragon-ball',
      'dragon ball fusion',
      'dragonball fusion world',
      'dbs-fusion-world',
      '6'
    ]
  },
  {
    id: 7,
    slug: 'union-arena',
    name: 'Union Arena',
    shortName: 'Union Arena',
    cardNexusName: 'Union Arena',
    cardNexusAliases: [
      'Union Arena',
      'union-arena',
      'Union Arena TCG',
      'union-arena-tcg',
      'UA',
      'unionarena'
    ],
    icon: 'fa-shield-halved',
    sauce: 'homura',
    aliases: [
      'union arena',
      'union-arena',
      'union',
      'ua',
      'unionarena',
      'union-arena-tcg',
      '7'
    ]
  },
  {
    id: 8,
    slug: 'star-wars',
    name: 'Star Wars: Unlimited',
    shortName: 'Star Wars',
    cardNexusName: 'Star Wars: Unlimited',
    cardNexusAliases: [
      'Star Wars: Unlimited',
      'star-wars-unlimited',
      'Star Wars Unlimited',
      'Star Wars: Unlimited TCG',
      'Star Wars Unlimited TCG',
      'star-wars',
      'Star Wars',
      'SWU'
    ],
    icon: 'fa-jedi',
    sauce: 'homura',
    aliases: [
      'star wars',
      'star wars unlimited',
      'star-wars',
      'star-wars-unlimited',
      'star wars: unlimited',
      'swu',
      'starwars',
      'star wars tcg',
      '8'
    ]
  },
  {
    id: 9,
    slug: 'yugioh',
    name: 'Yu-Gi-Oh!',
    shortName: 'Yu-Gi-Oh!',
    cardNexusName: 'Yu-Gi-Oh!',
    cardNexusAliases: [
      'Yu-Gi-Oh!',
      'yu-gi-oh!',
      'Yu-Gi-Oh! TCG',
      'yu-gi-oh-tcg',
      'Yu-Gi-Oh',
      'yu-gi-oh',
      'YuGiOh',
      'yugioh',
      'yugioh tcg',
      'YGO'
    ],
    icon: 'fa-cards',
    sauce: 'homura',
    aliases: [
      'yu-gi-oh!',
      'yu-gi-oh',
      'yugioh',
      'ygo',
      'yugioh!',
      'yu gi oh',
      'yu-gi-oh-tcg',
      'yugioh tcg',
      'yugi',
      '9',
      '5' // legacy ID in old client-side maps
    ]
  },
  {
    id: 10,
    slug: 'cardfight-vanguard',
    name: 'Cardfight!! Vanguard',
    shortName: 'Vanguard',
    cardNexusName: 'Cardfight!! Vanguard',
    cardNexusAliases: [
      'Cardfight!! Vanguard',
      'cardfight--vanguard',
      'Cardfight Vanguard',
      'cardfight-vanguard',
      'Cardfight!! Vanguard TCG',
      'Cardfight Vanguard TCG',
      'Vanguard',
      'vanguard',
      'CFV'
    ],
    icon: 'fa-sword',
    sauce: 'homura',
    aliases: [
      'cardfight vanguard',
      'cardfight!! vanguard',
      'cardfight-vanguard',
      'vanguard',
      'cfv',
      'cardfight',
      'card fight vanguard',
      'cardfight vanguard tcg',
      '10',
      '11' // legacy ID in old client-side maps
    ]
  },
  {
    id: 11,
    slug: 'fab',
    name: 'Flesh and Blood',
    shortName: 'FaB',
    cardNexusName: 'Flesh and Blood',
    cardNexusAliases: [
      'Flesh and Blood',
      'flesh-and-blood',
      'Flesh and Blood TCG',
      'flesh-and-blood-tcg',
      'Flesh & Blood',
      'flesh & blood',
      'FAB',
      'fab',
      'FABTCG'
    ],
    icon: 'fa-khanda',
    sauce: 'homura',
    aliases: [
      'flesh and blood',
      'flesh & blood',
      'fab',
      'flesh-and-blood',
      'fleshandblood',
      'fabtcg',
      '11',
      '12' // legacy ID in old client-side maps
    ]
  },
  {
    id: 12,
    slug: 'sorcery',
    name: 'Sorcery: Contested Realm',
    shortName: 'Sorcery',
    cardNexusName: 'Sorcery: Contested Realm',
    cardNexusAliases: [
      'Sorcery: Contested Realm',
      'sorcery-contested-realm',
      'Sorcery Contested Realm',
      'Sorcery',
      'sorcery',
      'Sorcery TCG',
      'sorcery-tcg',
      'SCR'
    ],
    icon: 'fa-scroll',
    sauce: 'homura',
    aliases: [
      'sorcery',
      'sorcery contested realm',
      'sorcery: contested realm',
      'scr',
      'sorcery-contested-realm',
      'sorcery tcg',
      '12',
      '13'
    ]
  },
  {
    id: 13,
    slug: 'riftbound',
    name: 'Riftbound',
    shortName: 'Riftbound',
    cardNexusName: 'Riftbound',
    cardNexusAliases: [
      'Riftbound',
      'riftbound',
      'Riftbound TCG',
      'riftbound-tcg',
      'Rift-Bound',
      'rift-bound',
      'Rift Bound'
    ],
    icon: 'fa-fire-pulse',
    sauce: 'homura',
    aliases: [
      'riftbound',
      'rift bound',
      'rift-bound',
      'riftbound tcg',
      'piltover',
      '13',
      '14'
    ]
  },
  {
    id: 14,
    slug: 'lorcana',
    name: 'Disney Lorcana',
    shortName: 'Lorcana',
    cardNexusName: 'Disney Lorcana',
    cardNexusAliases: [
      'Disney Lorcana',
      'disney-lorcana',
      'Disney Lorcana TCG',
      'disney-lorcana-tcg',
      'Lorcana',
      'lorcana',
      'Lorcana TCG',
      'lorcana-tcg',
      'Disney',
      'disney',
      'DL'
    ],
    icon: 'fa-wand-magic-sparkles',
    sauce: 'homura',
    aliases: [
      'lorcana',
      'disney lorcana',
      'disney-lorcana',
      'disney',
      'dl',
      'lorcana tcg',
      'disney lorcana tcg',
      '14',
      '8'
    ]
  },
  {
    id: 15,
    slug: 'universus',
    name: 'UniVersus',
    shortName: 'UniVersus',
    cardNexusName: 'UniVersus',
    cardNexusAliases: [
      'UniVersus',
      'universus',
      'UniVersus CCG',
      'universus-ccg',
      'UniVersus TCG',
      'universus-tcg',
      'My Hero Academia CCG',
      'My Hero Academia TCG',
      'My Hero Academia',
      'my-hero-academia',
      'UVS',
      'MHA'
    ],
    icon: 'fa-shield-halved',
    sauce: 'homura',
    aliases: [
      'universus',
      'universus tcg',
      'uvs',
      'my hero academia tcg',
      'mha',
      'my hero academia',
      'universus ccg',
      '15'
    ]
  },
  {
    id: 16,
    slug: 'hololive',
    name: 'Hololive',
    shortName: 'Hololive',
    cardNexusName: 'Hololive',
    cardNexusAliases: [
      'Hololive',
      'hololive',
      'Hololive Production',
      'hololive-production',
      'Hololive Official Card Game',
      'hololive-official-card-game',
      'Hololive OCDG',
      'Hololive TCG',
      'hololive-tcg',
      'Holo'
    ],
    icon: 'fa-star',
    sauce: 'homura',
    aliases: [
      'hololive',
      'hololive ocdg',
      'hololive production',
      'holo',
      'hololive tcg',
      '16'
    ]
  },
  {
    id: 17,
    slug: 'altered',
    name: 'Altered',
    shortName: 'Altered',
    cardNexusName: 'Altered',
    cardNexusAliases: [
      'Altered',
      'altered',
      'Altered TCG',
      'altered-tcg',
      'Altered Beyond the Gates',
      'altered-beyond-the-gates'
    ],
    icon: 'fa-compass',
    sauce: 'homura',
    aliases: [
      'altered',
      'altered tcg',
      'altered beyond the gates',
      '17'
    ]
  },
  {
    id: 18,
    slug: 'grand-archive',
    name: 'Grand Archive',
    shortName: 'Grand Archive',
    cardNexusName: 'Grand Archive TCG',
    cardNexusAliases: [
      'Grand Archive TCG',
      'grand-archive-tcg',
      'Grand Archive',
      'grand-archive',
      'GrandArchive',
      'grandarchive',
      'GA',
      'GATCG',
      'Omnidex'
    ],
    icon: 'fa-book-atlas',
    sauce: 'homura',
    aliases: [
      'grand archive',
      'grand archive tcg',
      'grand-archive-tcg',
      'grand-archive',
      'grandarchive',
      'ga',
      'gatcg',
      'omnidex',
      '18',
      '16'
    ]
  },
  {
    id: 19,
    slug: 'cyberpunk',
    name: 'Cyberpunk',
    shortName: 'Cyberpunk',
    cardNexusName: 'Cyberpunk',
    cardNexusAliases: [
      'Cyberpunk',
      'cyberpunk',
      'Cyberpunk Edgerunners',
      'cyberpunk-edgerunners',
      'Cyberpunk TCG',
      'cyberpunk-tcg',
      'CPR'
    ],
    icon: 'fa-microchip',
    sauce: 'homura',
    aliases: [
      'cyberpunk',
      'cyberpunk edgerunners',
      'cyberpunk-edgerunners',
      'cpr',
      'cyberpunk tcg',
      '19'
    ]
  },
  {
    id: 20,
    slug: 'godzilla',
    name: 'Godzilla',
    shortName: 'Godzilla',
    cardNexusName: 'Godzilla',
    cardNexusAliases: [
      'Godzilla',
      'godzilla',
      'Godzilla TCG',
      'godzilla-tcg',
      'Godzilla Card Game'
    ],
    icon: 'fa-radiation',
    sauce: 'homura',
    aliases: [
      'godzilla',
      'godzilla tcg',
      'godzilla card game',
      '20'
    ]
  }
];

/**
 * Normaliza uma string removendo diacríticos/acentos e pontuações para comparação robusta
 */
export function normalizeGameIdentifier(val: string | number | null | undefined): string {
  if (val === null || val === undefined) return '';
  return String(val)
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // remove acentos (ex: pokémon -> pokemon)
    .replace(/[^\w\s-]/g, '') // remove pontuação como :, !, etc.
    .replace(/\s+/g, ' ');
}

// Maps otimizados para busca rápida em O(1)
const GAME_BY_ID = new Map<number, CardGameDefinition>();
const GAME_BY_SLUG = new Map<string, CardGameDefinition>();
const GAME_BY_ALIAS = new Map<string, CardGameDefinition>();
export const CARDNEXUS_GAME_MAP = new Map<string, CardGameDefinition>();

// Inicialização dos índices
for (const game of CARD_GAMES) {
  GAME_BY_ID.set(game.id, game);
  GAME_BY_SLUG.set(game.slug.toLowerCase(), game);
  
  // Indexa o nome principal e shortName
  GAME_BY_ALIAS.set(normalizeGameIdentifier(game.name), game);
  if (game.shortName) {
    GAME_BY_ALIAS.set(normalizeGameIdentifier(game.shortName), game);
  }
  
  // Indexa a slug
  GAME_BY_ALIAS.set(normalizeGameIdentifier(game.slug), game);
  
  // Indexa todos os aliases
  for (const alias of game.aliases) {
    GAME_BY_ALIAS.set(normalizeGameIdentifier(alias), game);
    // Também indexa sem hífen
    GAME_BY_ALIAS.set(normalizeGameIdentifier(alias.replace(/[-_]/g, ' ')), game);
  }

  // Indexa especificamente aliases e nomes do CardNexus
  if (game.cardNexusName) {
    CARDNEXUS_GAME_MAP.set(normalizeGameIdentifier(game.cardNexusName), game);
    GAME_BY_ALIAS.set(normalizeGameIdentifier(game.cardNexusName), game);
  }
  if (game.cardNexusAliases) {
    for (const cna of game.cardNexusAliases) {
      CARDNEXUS_GAME_MAP.set(normalizeGameIdentifier(cna), game);
      CARDNEXUS_GAME_MAP.set(normalizeGameIdentifier(cna.replace(/[-_]/g, ' ')), game);
      GAME_BY_ALIAS.set(normalizeGameIdentifier(cna), game);
    }
  }
}

/**
 * Localiza a definição de um jogo a partir do nome ou slug usado pelo CardNexus (CSV ou JSON)
 */
export function findGameByCardNexus(cardNexusIdentifier: string | number | null | undefined): CardGameDefinition | undefined {
  if (!cardNexusIdentifier) return undefined;
  const norm = normalizeGameIdentifier(cardNexusIdentifier);
  const byNexus = CARDNEXUS_GAME_MAP.get(norm);
  if (byNexus) return byNexus;
  return findGame(cardNexusIdentifier);
}

/**
 * Verifica se um identificador de jogo vindo do CardNexus corresponde a um determinado jogo.
 * Suporta comparação por ID numérico, slug ou nome.
 */
export function isCardNexusGameMatch(
  cardNexusGame: string | number | null | undefined, 
  targetGame: string | number | null | undefined
): boolean {
  if (!cardNexusGame || !targetGame) return false;
  const gameFromNexus = findGameByCardNexus(cardNexusGame);
  if (!gameFromNexus) return isGame(cardNexusGame, targetGame);
  return isGame(gameFromNexus.id, targetGame) || isGame(gameFromNexus.slug, targetGame);
}

/**
 * Encontra a definição central de um jogo a partir de qualquer identificador:
 * ID numérico, slug, nome oficial, ou qualquer um de seus aliases/abreviações.
 */
export function findGame(identifier: number | string | null | undefined): CardGameDefinition | undefined {
  if (identifier === null || identifier === undefined || identifier === '') return undefined;

  // 1. Se for número ou string puramente numérica
  if (typeof identifier === 'number' || /^\d+$/.test(String(identifier).trim())) {
    const numId = Number(identifier);
    const byId = GAME_BY_ID.get(numId);
    if (byId) return byId;
  }

  const str = String(identifier).trim().toLowerCase();
  
  // 2. Busca exata por slug
  const bySlug = GAME_BY_SLUG.get(str);
  if (bySlug) return bySlug;

  // 3. Busca por índice normalizado de aliases e nomes
  const normalized = normalizeGameIdentifier(str);
  const byAlias = GAME_BY_ALIAS.get(normalized);
  if (byAlias) return byAlias;

  // 4. Busca parcial/heurística para casos comuns
  for (const game of CARD_GAMES) {
    if (game.slug === normalized) return game;
    if (normalizeGameIdentifier(game.name) === normalized) return game;
    if (game.shortName && normalizeGameIdentifier(game.shortName) === normalized) return game;
    for (const a of game.aliases) {
      const normA = normalizeGameIdentifier(a);
      if (normA === normalized) return game;
    }
  }

  // 5. Verificação de correspondência por contenção (ex: 'one piece card game' contém 'one piece')
  for (const game of CARD_GAMES) {
    const normName = normalizeGameIdentifier(game.name);
    if (normalized.includes(normName) || (normName.length > 4 && normName.includes(normalized))) {
      return game;
    }
  }

  return undefined;
}

/**
 * Retorna o jogo pelo seu ID numérico cadastrado no banco
 */
export function getGameById(id: number | string | null | undefined): CardGameDefinition | undefined {
  if (id === null || id === undefined) return undefined;
  return GAME_BY_ID.get(Number(id)) || findGame(id);
}

/**
 * Retorna o jogo pela sua slug canônica
 */
export function getGameBySlug(slug: string | null | undefined): CardGameDefinition | undefined {
  if (!slug) return undefined;
  return GAME_BY_SLUG.get(slug.toLowerCase().trim()) || findGame(slug);
}

/**
 * Retorna o nome oficial de exibição do jogo.
 * Ex: getGameName(1) -> 'One Piece'
 *     getGameName('pokemon') -> 'Pokémon'
 *     getGameName('mtg') -> 'Magic: The Gathering'
 */
export function getGameName(identifier: number | string | null | undefined, fallback?: string): string {
  const game = findGame(identifier);
  if (game) return game.name;
  if (fallback !== undefined) return fallback;
  return String(identifier || 'Card Game');
}

/**
 * Retorna o nome curto do jogo para exibições compactas.
 * Ex: getGameShortName(4) -> 'Magic'
 *     getGameShortName('dragon-ball-fusion') -> 'Dragon Ball'
 */
export function getGameShortName(identifier: number | string | null | undefined, fallback?: string): string {
  const game = findGame(identifier);
  if (game) return game.shortName || game.name;
  if (fallback !== undefined) return fallback;
  return getGameName(identifier);
}

/**
 * Retorna a slug canônica do jogo para APIs e URLs.
 * Ex: getGameSlug(3) -> 'pokemon'
 *     getGameSlug('Magic: The Gathering') -> 'magic'
 *     getGameSlug('optcg') -> 'one-piece'
 */
export function getGameSlug(identifier: number | string | null | undefined, fallback?: string): string {
  const game = findGame(identifier);
  if (game) return game.slug;
  if (fallback !== undefined) return fallback;
  if (typeof identifier === 'string') {
    return identifier.toLowerCase().trim().replace(/\s+/g, '-');
  }
  return 'one-piece';
}

/**
 * Retorna o ID numérico oficial do banco para o jogo.
 * Ex: getGameId('pokemon') -> 3
 *     getGameId('one-piece') -> 1
 */
export function getGameId(identifier: number | string | null | undefined, fallback: number = 1): number {
  const game = findGame(identifier);
  if (game) return game.id;
  const num = Number(identifier);
  return !isNaN(num) && num > 0 ? num : fallback;
}

/**
 * Retorna o ícone FontAwesome correspondente ao jogo
 */
export function getGameIcon(identifier: number | string | null | undefined): string {
  const game = findGame(identifier);
  if (game) return game.icon;
  return 'fa-gamepad';
}

/**
 * Retorna o backend de dados do jogo ('kyubey' para Magic, 'homura' para os demais)
 */
export function getGameSauce(identifier: number | string | null | undefined): 'homura' | 'kyubey' {
  if (identifier === 'kyubey') return 'kyubey';
  if (identifier === 'homura') return 'homura';
  const game = findGame(identifier);
  return game?.sauce || 'homura';
}

/**
 * Retorna a URL base oficial da API do jogo
 */
export function getGameApiBaseUrl(identifier: number | string | null | undefined): string {
  if (identifier === 'kyubey') return 'https://kyubey-proxy.vercel.app';
  const sauce = getGameSauce(identifier);
  if (sauce === 'kyubey') {
    return 'https://kyubey-proxy.vercel.app';
  }
  return 'https://homura-cards.vercel.app';
}

/**
 * Compara dois identificadores de jogo e retorna se correspondem ao mesmo jogo.
 * Ex: isGame(card.game_id, 'pokemon') -> true
 *     isGame('mtg', 4) -> true
 */
export function isGame(
  identifier: number | string | null | undefined,
  target: number | string | null | undefined
): boolean {
  if (identifier === null || identifier === undefined || target === null || target === undefined) {
    return false;
  }
  const gameA = findGame(identifier);
  const gameB = findGame(target);
  if (gameA && gameB) {
    return gameA.id === gameB.id;
  }
  return String(identifier).toLowerCase().trim() === String(target).toLowerCase().trim();
}

/**
 * Retorna todos os aliases conhecidos para um jogo.
 */
export function getGameAliases(identifier: number | string | null | undefined): string[] {
  const game = findGame(identifier);
  if (!game) return [];
  return [game.slug, game.name, ...(game.shortName ? [game.shortName] : []), ...game.aliases];
}

/**
 * Tenta inferir o jogo a partir de um bloco de texto, cabeçalho de decklist ou nome de arquivo.
 * Ex: detectGameFromText("// Pokémon Deck List") -> Pokémon definition
 *     detectGameFromText("Format: Standard One Piece TCG") -> One Piece definition
 */
export function detectGameFromText(text: string | null | undefined): CardGameDefinition | undefined {
  if (!text || typeof text !== 'string') return undefined;
  const sample = text.slice(0, 2000); // Primeiros 2000 caracteres costumam conter metadados e cabeçalhos

  // 1. Busca por padrões explícitos nos cabeçalhos ou comentários
  const headerMatch = sample.match(/(?:game|tcg|deck|format|sistema|jogo)\s*[:=]\s*([^\n\r,;]+)/i);
  if (headerMatch && headerMatch[1]) {
    const candidate = findGame(headerMatch[1].trim());
    if (candidate) return candidate;
  }

  // 2. Busca por menções diretas de jogos e seus aliases no texto
  // Prioriza jogos com nomes compostos ou siglas específicas
  for (const game of CARD_GAMES) {
    // Testa nome completo
    const nameRegex = new RegExp(`\\b${escapeRegExp(game.name)}\\b`, 'i');
    if (nameRegex.test(sample)) return game;

    // Testa slug
    const slugRegex = new RegExp(`\\b${escapeRegExp(game.slug)}\\b`, 'i');
    if (slugRegex.test(sample)) return game;

    // Testa aliases relevantes com pelo menos 3 caracteres para evitar falsos positivos
    for (const alias of game.aliases) {
      if (alias.length >= 3 && !/^\d+$/.test(alias)) {
        const aliasRegex = new RegExp(`\\b${escapeRegExp(alias)}\\b`, 'i');
        if (aliasRegex.test(sample)) return game;
      }
    }
  }

  return undefined;
}

function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Funções predicadas específicas para simplificar condicionais em toda a aplicação.
 */
export const isOnePieceGame = (game: number | string | null | undefined): boolean => isGame(game, 'one-piece');
export const isDigimonGame = (game: number | string | null | undefined): boolean => isGame(game, 'digimon');
export const isPokemonGame = (game: number | string | null | undefined): boolean => isGame(game, 'pokemon');
export const isMagicGame = (game: number | string | null | undefined): boolean => isGame(game, 'magic');
export const isGundamGame = (game: number | string | null | undefined): boolean => isGame(game, 'gundam');
export const isDragonBallGame = (game: number | string | null | undefined): boolean => isGame(game, 'dragon-ball-fusion');
export const isUnionArenaGame = (game: number | string | null | undefined): boolean => isGame(game, 'union-arena');
export const isStarWarsGame = (game: number | string | null | undefined): boolean => isGame(game, 'star-wars');
export const isYugiohGame = (game: number | string | null | undefined): boolean => isGame(game, 'yugioh');
export const isVanguardGame = (game: number | string | null | undefined): boolean => isGame(game, 'cardfight-vanguard');
export const isFabGame = (game: number | string | null | undefined): boolean => isGame(game, 'fab');
export const isSorceryGame = (game: number | string | null | undefined): boolean => isGame(game, 'sorcery');
export const isRiftboundGame = (game: number | string | null | undefined): boolean => isGame(game, 'riftbound');
export const isLorcanaGame = (game: number | string | null | undefined): boolean => isGame(game, 'lorcana');
export const isUniversusGame = (game: number | string | null | undefined): boolean => isGame(game, 'universus');
export const isHololiveGame = (game: number | string | null | undefined): boolean => isGame(game, 'hololive');
export const isAlteredGame = (game: number | string | null | undefined): boolean => isGame(game, 'altered');
export const isGrandArchiveGame = (game: number | string | null | undefined): boolean => isGame(game, 'grand-archive');
export const isCyberpunkGame = (game: number | string | null | undefined): boolean => isGame(game, 'cyberpunk');
export const isGodzillaGame = (game: number | string | null | undefined): boolean => isGame(game, 'godzilla');

/**
 * Lista canônica de todos os jogos suportados
 */
export const ALL_GAMES: readonly CardGameDefinition[] = CARD_GAMES;
export const ALL_GAME_SLUGS: readonly string[] = CARD_GAMES.map(g => g.slug);
export const ALL_GAME_NAMES: readonly string[] = CARD_GAMES.map(g => g.name);
export const ALL_GAME_IDS: readonly number[] = CARD_GAMES.map(g => g.id);

/**
 * Mapa rápido ID -> Slug para compatibilidade retroativa
 */
export const GAME_ID_TO_SLUG: Record<string, string> = Object.fromEntries(
  CARD_GAMES.map(g => [String(g.id), g.slug])
);

/**
 * Mapa rápido Slug -> ID
 */
export const GAME_SLUG_TO_ID: Record<string, number> = Object.fromEntries(
  CARD_GAMES.map(g => [g.slug, g.id])
);

/**
 * Mapa rápido Slug -> Nome oficial
 */
export const GAME_SLUG_TO_NAME: Record<string, string> = Object.fromEntries(
  CARD_GAMES.map(g => [g.slug, g.name])
);

/**
 * Compatibilidade com código existente:
 * Funções delegadas para garantir interoperabilidade total sem quebras.
 */
export const getGameNameById = (id: number | string): string => getGameName(id);
export const getSlugByGame = (game: string | number): string => getGameSlug(game);
export const resolveGameSlug = (game: number | string | undefined | null): string => getGameSlug(game);
export const getGameIconClass = (game: string | number): string => getGameIcon(game);

